import { PlusIcon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { AppHeader } from "~/components/AppHeader"
import { KBDocumentList } from "~/components/knowledgeBase/KBDocumentList"
import { LoadingState } from "~/components/screens/StateScreen"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import { TeamPermissions } from "~/lib/permissions"
import { api } from "~/trpc/react"

export default function Page() {
	const { team, isLoading } = useTeam()
	const { hasTeamPermission } = usePermission()
	const navigate = useNavigate()
	const canEdit = hasTeamPermission(TeamPermissions.EDIT_KNOWLEDGE_BASE)
	const utils = api.useUtils()
	const documents = api.knowledgeBase.list.useQuery(
		{ teamId: team?.id ?? "" },
		{ enabled: !!team?.id }
	)
	const createDocument = api.knowledgeBase.create.useMutation({
		onSuccess: async (document) => {
			await utils.knowledgeBase.list.invalidate({ teamId: team?.id ?? "" })
			if (document) navigate(document.slug)
		}
	})

	if (!team && !isLoading) return null

	return (
		<main className="flex h-full flex-col">
			<AppHeader
				contentClassName="flex min-w-0 items-center gap-1.5"
				actions={
					team && canEdit ? (
						<Button
							size="sm"
							disabled={createDocument.isPending}
							onClick={() =>
								createDocument.mutate({
									teamId: team.id,
									title: "Untitled document"
								})
							}
						>
							<PlusIcon className="size-4" />
							New page
						</Button>
					) : null
				}
			>
				{team ? (
					<>
						<EntityIdentity kind="team" name={team.name} image={team.logo} />
						<span className="text-text-sub">/</span>
						<h1 className="font-medium text-text-main">Knowledge Base</h1>
					</>
				) : null}
			</AppHeader>
			<div className="min-h-0 flex-1 overflow-hidden px-3 py-4 sm:px-5 lg:px-6">
				{isLoading || documents.isLoading ? (
					<LoadingState className="bg-transparent" />
				) : (
					<KBDocumentList
						documents={documents.data ?? []}
						onCreate={
							canEdit
								? (category) => {
										if (!team) return
										createDocument.mutate({
											teamId: team.id,
											title: "Untitled document",
											category
										})
									}
								: undefined
						}
					/>
				)}
			</div>
		</main>
	)
}
